import { IIssueFaultTypeChild, IssueHardwareType } from '@kcftech/prometheus-client';
import { useQuery } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { usePopup } from 'custom_hooks/usePopup';
import { FaultTypeLozenge } from 'pages/Desk/issues/fault_type/FaultTypeLozenge';
import { FaultTypePopup } from 'pages/Desk/issues/fault_type/FaultTypePopup';
import { InputCurrentValue, InputTrigger } from 'pages/Desk/issues/fault_type/styled';
import { IssueFaultType } from 'pages/Desk/issues/fault_type/useFaultTypes';
import { DefaultIssueMode, IssueMode } from 'pages/Desk/issues/shared';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { SecondaryText } from 'styles/theme_styles/text';
import { sortBy } from 'utils/array';
import { Filter } from 'utils/query_v3';

const paging = {
    page: 0,
    pageLimit: 1000,
};

export type FaultTypeOption = Readonly<
    Pick<IIssueFaultTypeChild, 'Id' | 'Name' | 'FullName'>
>;

export type FaultTypesInputProps = Readonly<
    {
        id?: string;
        onChange: (newFaultTypes: IssueFaultType[]) => void;
        isDisabled?: boolean;
        style?: React.CSSProperties;
        className?: string;
        issueMode?: IssueMode;
        hardwareType?: IssueHardwareType;
        disableRootLevel?: boolean;
        /**
         * If true, for hardware health issues, the input will be
         * restricted to only fault types for manual creation.
         */
        isCreating?: boolean;
        /**
         * if true, hides internal fault types
         */
        hideInternal?: boolean;
        /**
         * If true, at least one fault type must be selected.
         */
        requireAtLeastOne?: boolean;
    } & (
        | {
              faultTypeIds: string[];
              faultTypes?: never;
          }
        | {
              faultTypeIds?: never;
              faultTypes: FaultTypeOption[];
          }
    )
>;

export function FaultTypesInput({
    id,
    faultTypeIds,
    faultTypes,
    onChange,
    isDisabled = false,
    style,
    className,
    issueMode = DefaultIssueMode,
    disableRootLevel = false,
    hardwareType,
    isCreating,
    hideInternal,
    requireAtLeastOne,
}: FaultTypesInputProps) {
    const { isOpen, toggleOpen, close, popupRef, triggerRef } = usePopup();

    const { data: initialFaultTypes } = useQuery({
        queryKey: ['initial-fault-types', ...(faultTypeIds ?? [])],

        queryFn: () =>
            issueMode === 'machineHealth'
                ? sdClient.getIssueFaultTypes({
                      filter: Filter.in('Id', faultTypeIds as string[]),
                      ...paging,
                  })
                : sdClient.getIssueHardwareHealthFaultTypes({
                      filter: Filter.and(
                          Filter.in('Id', faultTypeIds as string[]),
                          hardwareType
                              ? Filter.eq('HardwareType', hardwareType)
                              : undefined,
                          isCreating ? Filter.eq('ForManualCreation', true) : undefined,
                      ),
                      ...paging,
                  }),
        enabled: faultTypeIds !== undefined && faultTypeIds.length > 0,
    });

    const orderedSelectedFaultTypes = useMemo(
        () =>
            sortBy(
                faultTypes ?? initialFaultTypes?.Result ?? [],
                ({ FullName }) => FullName,
            ),
        [faultTypes, initialFaultTypes?.Result],
    );

    const onClose = (newFaultTypes?: IssueFaultType[]) => {
        close();
        if (newFaultTypes) {
            onChange(newFaultTypes);
        }
    };

    useEffect(() => {
        // Close popup if input is disabled
        if (isDisabled && isOpen) {
            close();
        }
    }, [close, isDisabled, isOpen]);

    return (
        <>
            <InputTrigger
                id={id}
                ref={triggerRef}
                disabled={isDisabled}
                onClick={toggleOpen}
                style={style}
                className={className}
            >
                <InputCurrentValue>
                    {orderedSelectedFaultTypes.length === 0 && (
                        <SecondaryText>Select Fault</SecondaryText>
                    )}
                    {orderedSelectedFaultTypes.map((faultType) => (
                        <FaultTypeLozenge key={faultType.Id} faultType={faultType} />
                    ))}
                </InputCurrentValue>
            </InputTrigger>
            {isOpen && (
                <FaultTypePopup
                    ref={popupRef}
                    anchorRef={triggerRef}
                    initialFaultIds={orderedSelectedFaultTypes.map((ft) => ft.Id)}
                    onClose={onClose}
                    issueMode={issueMode}
                    disableRootLevel={disableRootLevel}
                    hardwareType={hardwareType}
                    isCreating={isCreating}
                    hideInternal={hideInternal}
                    requireAtLeastOne={requireAtLeastOne}
                />
            )}
        </>
    );
}
